/**
 * IncidentReportingPage.jsx
 *
 * Tabbed page for Operations Incident Reporting.
 * Three tabs:
 *   1. Report Incident — capture a new incident (property-access filtered)
 *   2. Incident Reports — view/filter existing incidents (property-access filtered)
 *   3. Trend Analysis — charts showing incident trends over time
 *
 * Follows the same tabbed pattern as ChecklistReportingPage.
 *
 * @module OperationsPages/IncidentReportingPage
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    FaFileAlt, FaSpinner, FaDownload, FaFilter, FaExclamationTriangle,
    FaChevronLeft, FaChevronRight, FaPlusCircle, FaListAlt, FaChartLine,
    FaCalendarAlt, FaEdit, FaSave
} from 'react-icons/fa';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import '@/styles/incidentReportingStyles.scss';
import * as Sentry from '@sentry/react';

// ─── Constants ───────────────────────────────────────────────────────────────

const TAG_OPTIONS = [
    "Armed Robbery",
    "BreakIn",
    "Assault/Injury",
    "DamageToProperty",
    "DeathOnSite",
    "Theft",
    "RiotOrLooting"
];

const DISPLAY_TAG_LABELS = {
    "Armed Robbery": "Armed Robbery",
    "BreakIn": "Break In",
    "Assault/Injury": "Assault / Injury",
    "DamageToProperty": "Damage To Property",
    "DeathOnSite": "Death On Site",
    "Theft": "Theft",
    "RiotOrLooting": "Riot / Looting"
};

const TAG_LABELS_LIST = {
    ArmedRobbery: 'Armed Robbery',
    BreakIn: 'Break In',
    AssaultOrInjury: 'Assault / Injury',
    DamageToProperty: 'Damage To Property',
    DeathOnSite: 'Death On Site',
    Theft: 'Theft',
    RiotOrLooting: 'Riot / Looting'
};

const MONTH_NAMES = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const CHART_COLORS = ['#e74c3c', '#f39c12', '#3498db', '#2ecc71', '#9b59b6', '#1abc9c'];

/**
 * Fallback file storage configuration used before the server response arrives
 * or if the config endpoint is unavailable.
 */
const DEFAULT_FILE_STORAGE_CONFIG = {
    maxFileSizeBytes: 100 * 1024 * 1024, // 100 MB
    allowedExtensions: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt,.csv,.zip,.mp4,.mov,.avi,.mkv,.webm'
};

// ─── Helper Functions ────────────────────────────────────────────────────────

const formatPeriod = (period) => {
    const year = Math.floor(period / 100);
    const month = period % 100;
    return `${MONTH_NAMES[month] || ''} ${year}`;
};

const getIncidentTags = (incident) => {
    const tags = [];
    if (incident.armedRobbery) tags.push('Armed Robbery');
    if (incident.breakIn) tags.push('Break In');
    if (incident.assaultOrInjury) tags.push('Assault / Injury');
    if (incident.damageToProperty) tags.push('Damage To Property');
    if (incident.deathOnSite) tags.push('Death On Site');
    if (incident.theft) tags.push('Theft');
    if (incident.riotOrLooting) tags.push('Riot / Looting');
    return tags;
};

const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-GB', { month: 'long' });
    return `${day} ${month} ${d.getFullYear()}`;
};

// Promoted to module scope so both ReportIncidentTab and IncidentReportsTab can use them
const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const iconMap = {
        pdf: '📄', doc: '📃', docx: '📃', xls: '📊', xlsx: '📊',
        jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️',
        txt: '📝', csv: '📝', zip: '📦',
        mp4: '🎬', mov: '🎬', avi: '🎬', mkv: '🎬', webm: '🎬'
    };
    return iconMap[ext] || '📁';
};

// ─── Report Incident Tab ─────────────────────────────────────────────────────

const ReportIncidentTab = ({ accessibleProperties }) => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const { apiFetch } = useAuth();

    const [incidentDate, setIncidentDate] = useState(todayStr);
    // null = user hasn't made an explicit selection; fall back to first accessible property
    const [selectedPropertyOverride, setSelectedPropertyOverride] = useState(null);
    const property = selectedPropertyOverride ?? accessibleProperties[0]?.propertyName ?? '';
    const setProperty = (val) => setSelectedPropertyOverride(val);
    const [tags, setTags] = useState([]);
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = React.useRef(null);
    const dateInputRef = React.useRef(null);

    // File storage configuration fetched from the server so that accepted extensions and
    // size limits stay in sync with the backend without manual updates on the client.
    const [fileStorageConfig, setFileStorageConfig] = useState(DEFAULT_FILE_STORAGE_CONFIG);

    const maxDate = todayStr;

    // Fetch file storage config once on mount
    useEffect(() => {
        const fetchFileStorageConfig = async () => {
            try {
                const response = await apiFetch('/api/Settings/file-storage-config');
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.data) {
                        setFileStorageConfig({
                            maxFileSizeBytes: data.data.maxFileSizeBytes ?? DEFAULT_FILE_STORAGE_CONFIG.maxFileSizeBytes,
                            allowedExtensions: data.data.allowedExtensions ?? DEFAULT_FILE_STORAGE_CONFIG.allowedExtensions
                        });
                    }
                }
            } catch (err) {
                Sentry.captureException(err);
                // Silently fall back to the default configuration
            }
        };
        fetchFileStorageConfig();
    }, [apiFetch]);

    // Build a comma-separated accept string for the file input from the server config
    const acceptedExtensions = useMemo(
        () => fileStorageConfig.allowedExtensions,
        [fileStorageConfig]
    );

    // Human-readable max size label (e.g. "100 MB")
    const maxFileSizeLabel = useMemo(() => {
        const mb = Math.round(fileStorageConfig.maxFileSizeBytes / (1024 * 1024));
        return `${mb} MB`;
    }, [fileStorageConfig]);

    const handleTagChange = (e) => {
        const { value, checked } = e.target;
        setTags(prev => checked ? [...prev, value] : prev.filter(tag => tag !== value));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        setSelectedFiles(prev => [...prev, ...files]);
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        setSelectedFiles(prev => [...prev, ...Array.from(e.dataTransfer.files || [])]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!incidentDate || !property || tags.length === 0) {
            toast.error("Please fill in all fields and select at least one tag.");
            return;
        }

        const oversizedFiles = selectedFiles.filter(
            file => file.size > fileStorageConfig.maxFileSizeBytes
        );

        if (oversizedFiles.length > 0) {
            toast.error(`The following file(s) exceed the ${maxFileSizeLabel} size limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
            return;
        }

        const dateObj = new Date(incidentDate + 'T00:00:00');
        const year = dateObj.getFullYear().toString();
        const month = dateObj.toLocaleString('en-US', { month: 'long' });
        const day = dateObj.getDate();

        setLoading(true);

        const requestBody = {
            Year: year,
            Month: month,
            Day: day,
            PropertyName: property,
            Tags: tags,
            Description: description || null
        };

        try {
            const response = await apiFetch('/api/Ops/OperationIncidents', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                const incidentId = data.data?.incidentId;

                if (selectedFiles.length > 0 && incidentId) {
                    const formData = new FormData();
                    selectedFiles.forEach(file => formData.append('file', file));
                    if (description) formData.append('description', description);

                    try {
                        const uploadResponse = await apiFetch(
                            `/api/Ops/Incidents/${incidentId}/documents/upload`,
                            { method: 'POST', body: formData }
                        );
                        const uploadData = await uploadResponse.json();
                        if (uploadData.success) {
                            toast.success(`Incident saved with ${selectedFiles.length} file(s) uploaded!`);
                        } else {
                            toast.warning("Incident saved but file upload failed: " + (uploadData.message || "Unknown error"));
                        }
                    } catch (catchError) {
                        Sentry.captureException(catchError);
                        toast.warning("Incident saved but file upload encountered an error.");
                    }
                } else {
                    toast.success("Incident saved successfully!");
                }

                setTags([]);
                setDescription('');
                setSelectedFiles([]);
                setIncidentDate(todayStr);
                setSelectedPropertyOverride(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            } else {
                toast.error(data.message || "Failed to save incident.");
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error("An error occurred while saving the incident.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form className="incident-form-tab" onSubmit={handleSubmit}>
            <div className="form-grid">
                <div className="form-group">
                    <label htmlFor="incidentDate">Incident Date:</label>
                    <div className="date-display-wrapper">
                        <span className={`date-display-text${!incidentDate ? ' placeholder' : ''}`}>
                            {incidentDate ? formatDate(incidentDate + 'T00:00:00') : 'Select date'}
                        </span>
                        <FaCalendarAlt className="date-icon" />
                        <input
                            type="date"
                            id="incidentDate"
                            ref={dateInputRef}
                            className="date-hidden"
                            value={incidentDate}
                            max={maxDate}
                            onClick={() => dateInputRef.current?.showPicker?.()}
                            onChange={e => setIncidentDate(e.target.value)}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="property">Property:</label>
                    <select id="property" value={property} onChange={e => setProperty(e.target.value)}>
                        {accessibleProperties.length > 0 ? (
                            accessibleProperties.map(p => (
                                <option key={p.propertyID} value={p.propertyName}>{p.propertyName}</option>
                            ))
                        ) : (
                            <option value="">No properties available</option>
                        )}
                    </select>
                </div>
            </div>

            <div className="form-group">
                <label>Tags:</label>
                <div className="tags-row">
                    {TAG_OPTIONS.map(tag => (
                        <label key={tag} className="tag-checkbox">
                            <input
                                type="checkbox"
                                value={tag}
                                checked={tags.includes(tag)}
                                onChange={handleTagChange}
                            />
                            {DISPLAY_TAG_LABELS[tag]}
                        </label>
                    ))}
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="description">Description:</label>
                <textarea
                    id="description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Optional description of the incident..."
                    rows={3}
                />
            </div>

            <div className="form-group">
                <label>Attach Files:</label>
                <div
                    className={`file-dropzone${isDragging ? ' dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        accept={acceptedExtensions}
                        hidden
                    />
                    <div className="dropzone-content">
                        <span className="dropzone-icon">📂</span>
                        <p className="dropzone-text">Drag &amp; drop files here or <span className="dropzone-browse">browse</span></p>
                        <p className="dropzone-hint">PDF, Word, Excel, PowerPoint, Images, CSV, ZIP, Video · Max size: {maxFileSizeLabel}</p>
                    </div>
                </div>
                {selectedFiles.length > 0 && (
                    <div className="file-list">
                        <div className="file-list-header">
                            <span>{selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected</span>
                            <button type="button" className="clear-all-btn" onClick={() => { setSelectedFiles([]); if (fileInputRef.current) fileInputRef.current.value = ''; }}>Clear all</button>
                        </div>
                        {selectedFiles.map((file, idx) => (
                            <div key={idx} className="file-item">
                                <span className="file-icon">{getFileIcon(file.name)}</span>
                                <div className="file-details">
                                    <span className="file-name">{file.name}</span>
                                    <span className="file-size">{formatFileSize(file.size)}</span>
                                </div>
                                <button type="button" className="file-remove-btn" onClick={() => removeFile(idx)} title="Remove file">✕</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? "Saving..." : "Save Incident"}
            </button>
        </form>
    );
};

// ─── Incident Reports Tab ────────────────────────────────────────────────────

const IncidentReportsTab = ({ accessiblePropertyNames, canEdit }) => {
    const { apiFetch } = useAuth();

    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [filterProperty, setFilterProperty] = useState('');
    const [filterTag, setFilterTag] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterMonth, setFilterMonth] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // Edit state — tracks which row is being edited and the draft values
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    // ── Edit file-upload state ────────────────────────────────────────────────
    const [fileStorageConfig, setFileStorageConfig] = useState(DEFAULT_FILE_STORAGE_CONFIG);
    const [editFiles, setEditFiles] = useState([]);
    const [editIsDragging, setEditIsDragging] = useState(false);
    const editFileInputRef = React.useRef(null);

    const editAcceptedExtensions = useMemo(
        () => fileStorageConfig.allowedExtensions,
        [fileStorageConfig]
    );

    const editMaxFileSizeLabel = useMemo(() => {
        const mb = Math.round(fileStorageConfig.maxFileSizeBytes / (1024 * 1024));
        return `${mb} MB`;
    }, [fileStorageConfig]);

    // Fetch file storage config once on mount
    useEffect(() => {
        let active = true;
        const fetchConfig = async () => {
            try {
                const response = await apiFetch('/api/Settings/file-storage-config');
                if (response.ok) {
                    const data = await response.json();
                    if (active && data.success && data.data) {
                        setFileStorageConfig({
                            maxFileSizeBytes: data.data.maxFileSizeBytes ?? DEFAULT_FILE_STORAGE_CONFIG.maxFileSizeBytes,
                            allowedExtensions: data.data.allowedExtensions ?? DEFAULT_FILE_STORAGE_CONFIG.allowedExtensions
                        });
                    }
                }
            } catch (err) {
                Sentry.captureException(err);
            }
        };
        fetchConfig();
        return () => { active = false; };
    }, [apiFetch]);

    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let y = currentYear; y >= 2020; y--) yearOptions.push(y);

    // Inline fetch — avoids cascading-setState lint issues with useCallback+useEffect pattern
    useEffect(() => {
        let active = true;
        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams();
                if (filterProperty) params.append('propertyName', filterProperty);
                if (filterTag) params.append('tag', filterTag);
                if (filterYear) params.append('year', filterYear);
                if (filterMonth) params.append('month', filterMonth);
                params.append('page', page);
                params.append('pageSize', pageSize);

                const response = await apiFetch(`/api/Ops/Incidents?${params.toString()}`);
                const data = await response.json();
                if (!active) return;

                if (data.success) {
                    setIncidents(data.data.incidents || []);
                    setTotalCount(data.data.totalCount || 0);
                    setTotalPages(data.data.totalPages || 1);
                } else {
                    setError(data.message || 'Failed to load incidents');
                }
            } catch (catchError) {
                if (!active) return;
                Sentry.captureException(catchError);
                setError('Failed to load incidents. Please try again.');
            } finally {
                if (active) setLoading(false);
            }
        };
        run();
        return () => { active = false; };
    }, [apiFetch, filterProperty, filterTag, filterYear, filterMonth, page, pageSize, refreshKey]);

    // setPage(1) lives in event handlers, not a useEffect, to avoid cascading renders
    const handlePropertyChange = (e) => { setFilterProperty(e.target.value); setPage(1); };
    const handleTagChange = (e) => { setFilterTag(e.target.value); setPage(1); };
    const handleYearChange = (e) => { setFilterYear(e.target.value); setPage(1); };
    const handleMonthChange = (e) => { setFilterMonth(e.target.value); setPage(1); };

    const clearFilters = () => {
        setFilterProperty('');
        setFilterTag('');
        setFilterYear('');
        setFilterMonth('');
        setPage(1);
    };

    /**
     * Converts an incident object into an editable draft with separate year/month/day
     * and a tags array, matching the structure expected by the update API.
     */
    const buildEditDraft = (incident) => {
        const year = Math.floor(incident.period / 100);
        const month = incident.period % 100;
        const tags = [];
        if (incident.armedRobbery) tags.push("Armed Robbery");
        if (incident.breakIn) tags.push("BreakIn");
        if (incident.assaultOrInjury) tags.push("Assault/Injury");
        if (incident.damageToProperty) tags.push("DamageToProperty");
        if (incident.deathOnSite) tags.push("DeathOnSite");
        if (incident.theft) tags.push("Theft");
        if (incident.riotOrLooting) tags.push('Riot / Looting');

        return {
            year: year.toString(),
            month: MONTH_NAMES[month] || '',
            day: incident.dayOfMonth ?? '',
            propertyName: incident.propertyName || '',
            tags,
            description: incident.description || ''
        };
    };

    /** Start editing a row */
    const handleEditClick = (e, incident) => {
        e.stopPropagation();
        setEditingId(incident.id);
        setEditDraft(buildEditDraft(incident));
        setEditFiles([]);
        if (editFileInputRef.current) editFileInputRef.current.value = '';
        // Collapse any expanded document row
        setExpandedId(null);
    };

    /** Cancel editing */
    const handleCancelEdit = () => {
        setEditingId(null);
        setEditDraft(null);
        setEditFiles([]);
        if (editFileInputRef.current) editFileInputRef.current.value = '';
    };

    /** Save edited incident via PUT, then upload any new files */
    const handleSaveEdit = async () => {
        if (!editDraft || !editingId) return;
        if (!editDraft.propertyName || editDraft.tags.length === 0) {
            toast.error("Property and at least one tag are required.");
            return;
        }

        const oversizedFiles = editFiles.filter(f => f.size > fileStorageConfig.maxFileSizeBytes);
        if (oversizedFiles.length > 0) {
            toast.error(`The following file(s) exceed the ${editMaxFileSizeLabel} size limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
            return;
        }

        setSaving(true);
        try {
            const response = await apiFetch(`/api/Ops/Incidents/${editingId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Year: editDraft.year,
                    Month: editDraft.month,
                    Day: editDraft.day ? parseInt(editDraft.day) : null,
                    PropertyName: editDraft.propertyName,
                    Tags: editDraft.tags,
                    Description: editDraft.description
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Upload new files if any were attached
                if (editFiles.length > 0) {
                    const formData = new FormData();
                    editFiles.forEach(file => formData.append('file', file));
                    try {
                        const uploadResponse = await apiFetch(
                            `/api/Ops/Incidents/${editingId}/documents/upload`,
                            { method: 'POST', body: formData }
                        );
                        const uploadData = await uploadResponse.json();
                        if (uploadData.success) {
                            toast.success(`Incident updated with ${editFiles.length} file(s) uploaded!`);
                        } else {
                            toast.warning("Incident updated but file upload failed: " + (uploadData.message || "Unknown error"));
                        }
                    } catch (uploadErr) {
                        Sentry.captureException(uploadErr);
                        toast.warning("Incident updated but file upload encountered an error.");
                    }
                } else {
                    toast.success("Incident updated successfully.");
                }

                // Update the local row so the table reflects changes immediately
                setIncidents(prev => prev.map(inc => {
                    if (inc.id !== editingId) return inc;
                    const monthNum = MONTH_NAMES.indexOf(editDraft.month);
                    return {
                        ...inc,
                        period: parseInt(editDraft.year) * 100 + monthNum,
                        dayOfMonth: editDraft.day ? parseInt(editDraft.day) : null,
                        propertyName: editDraft.propertyName,
                        armedRobbery: editDraft.tags.includes("Armed Robbery") ? 1 : null,
                        breakIn: editDraft.tags.includes("BreakIn") ? 1 : null,
                        assaultOrInjury: editDraft.tags.includes("Assault/Injury") ? 1 : null,
                        damageToProperty: editDraft.tags.includes("DamageToProperty") ? 1 : null,
                        deathOnSite: editDraft.tags.includes("DeathOnSite") ? 1 : null,
                        theft: editDraft.tags.includes("Theft") ? 1 : null,
                        riotOrLooting: editDraft.tags.includes("RiotOrLooting") ? 1 : null,
                        description: editDraft.description
                    };
                }));
                setEditingId(null);
                setEditDraft(null);
                setEditFiles([]);
                if (editFileInputRef.current) editFileInputRef.current.value = '';
                // Trigger a fresh fetch so the table reflects the real updated document count
                setRefreshKey(k => k + 1);
            } else {
                toast.error(data.message || "Failed to update incident.");
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error("An error occurred while updating the incident.");
        } finally {
            setSaving(false);
        }
    };

    /** Toggle a tag in the edit draft */
    const toggleEditTag = (tag) => {
        setEditDraft(prev => ({
            ...prev,
            tags: prev.tags.includes(tag)
                ? prev.tags.filter(t => t !== tag)
                : [...prev.tags, tag]
        }));
    };

    const handleEditFileChange = (e) => {
        setEditFiles(prev => [...prev, ...Array.from(e.target.files || [])]);
    };

    const handleEditFileDrop = (e) => {
        e.preventDefault();
        setEditIsDragging(false);
        setEditFiles(prev => [...prev, ...Array.from(e.dataTransfer.files || [])]);
    };

    const removeEditFile = (index) => {
        setEditFiles(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="incident-reports-tab">
            <div className="filters-section">
                <div className="filter-controls">
                    <div className="filter-group">
                        <label><FaFilter /> Property</label>
                        <select value={filterProperty} onChange={handlePropertyChange}>
                            <option value="">All Properties</option>
                            {accessiblePropertyNames.map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Tag</label>
                        <select value={filterTag} onChange={handleTagChange}>
                            <option value="">All Tags</option>
                            {Object.entries(TAG_LABELS_LIST).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Year</label>
                        <select value={filterYear} onChange={handleYearChange}>
                            <option value="">All Years</option>
                            {yearOptions.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Month</label>
                        <select value={filterMonth} onChange={handleMonthChange} disabled={!filterYear}>
                            <option value="">All Months</option>
                            {MONTH_NAMES.slice(1).map((m, idx) => (
                                <option key={idx + 1} value={idx + 1}>{m}</option>
                            ))}
                        </select>
                    </div>
                    <button className="btn-clear-filters" onClick={clearFilters} title="Clear all filters">Clear</button>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger">
                    <FaExclamationTriangle /> {error}
                </div>
            )}

            {loading && (
                <div className="loading-container">
                    <FaSpinner className="fa-spin fa-2x" />
                    <p>Loading incidents...</p>
                </div>
            )}

            {!loading && !error && (
                <>
                    <div className="record-count-bar">
                        <span>{totalCount} incident{totalCount !== 1 ? 's' : ''} found</span>
                    </div>
                    <div className="table-responsive">
                        <table className="incident-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Property</th>
                                    <th>Tags</th>
                                    <th>Description</th>
                                    <th>Files</th>
                                    <th>Created Date</th>
                                    {canEdit && <th>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {incidents.length === 0 ? (
                                    <tr>
                                        <td colSpan={canEdit ? 7 : 6} className="empty-state">
                                            No incidents found matching the current filters.
                                        </td>
                                    </tr>
                                ) : (
                                    incidents.map(incident => {
                                        const incidentTags = getIncidentTags(incident);
                                        const isExpanded = expandedId === incident.id;
                                        const isEditing = editingId === incident.id;
                                        const hasDocuments = incident.documents && incident.documents.length > 0;
                                        const colSpan = canEdit ? 7 : 6;
                                        return (
                                            <React.Fragment key={incident.id}>
                                                <tr
                                                    className={`incident-row ${hasDocuments ? 'has-docs' : ''} ${isExpanded ? 'expanded' : ''} ${isEditing ? 'editing' : ''} ${canEdit ? 'editable' : ''}`}
                                                    onClick={() => {
                                                        if (isEditing) return;
                                                        if (hasDocuments) setExpandedId(prev => prev === incident.id ? null : incident.id);
                                                    }}
                                                    style={{ cursor: isEditing ? 'default' : (hasDocuments ? 'pointer' : 'default') }}
                                                >
                                                    <td>{incident.dayOfMonth ? `${incident.dayOfMonth} ${formatPeriod(incident.period)}` : formatPeriod(incident.period)}</td>
                                                    <td>{incident.propertyName}</td>
                                                    <td>
                                                        <div className="tag-badges">
                                                            {incidentTags.map(tag => (
                                                                <span key={tag} className={`tag-badge tag-${tag.replace(/[\s/]/g, '-').toLowerCase()}`}>
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="description-cell">{incident.description || '-'}</td>
                                                    <td>
                                                        {hasDocuments ? (
                                                            <span className="file-count"><FaFileAlt /> {incident.documents.length}</span>
                                                        ) : (
                                                            <span className="no-files">-</span>
                                                        )}
                                                    </td>
                                                    <td>{formatDate(incident.createdDate)}</td>
                                                    {canEdit && (
                                                        <td>
                                                            <button
                                                                className="btn-edit-row"
                                                                onClick={(e) => handleEditClick(e, incident)}
                                                                title="Edit this incident"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>

                                                {/* Inline edit row — shown below the data row when editing */}
                                                {isEditing && editDraft && (
                                                    <tr className="edit-row">
                                                        <td colSpan={colSpan}>
                                                            {/* Date fields */}
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                                                <div className="edit-field">
                                                                    <label>Year</label>
                                                                    <select
                                                                        value={editDraft.year}
                                                                        onChange={e => setEditDraft(prev => ({ ...prev, year: e.target.value }))}
                                                                    >
                                                                        {yearOptions.map(y => (
                                                                            <option key={y} value={y}>{y}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div className="edit-field">
                                                                    <label>Month</label>
                                                                    <select
                                                                        value={editDraft.month}
                                                                        onChange={e => setEditDraft(prev => ({ ...prev, month: e.target.value }))}
                                                                    >
                                                                        {MONTH_NAMES.slice(1).map(m => (
                                                                            <option key={m} value={m}>{m}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div className="edit-field">
                                                                    <label>Day</label>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        max="31"
                                                                        value={editDraft.day}
                                                                        onChange={e => setEditDraft(prev => ({ ...prev, day: e.target.value }))}
                                                                        placeholder="Day"
                                                                    />
                                                                </div>
                                                                <div className="edit-field">
                                                                    <label>Property</label>
                                                                    <select
                                                                        value={editDraft.propertyName}
                                                                        onChange={e => setEditDraft(prev => ({ ...prev, propertyName: e.target.value }))}
                                                                    >
                                                                        {accessiblePropertyNames.map(name => (
                                                                            <option key={name} value={name}>{name}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            {/* Tags */}
                                                            <div className="edit-field" style={{ marginBottom: '0.5rem' }}>
                                                                <label>Tags</label>
                                                                <div className="edit-tags">
                                                                    {TAG_OPTIONS.map(tag => (
                                                                        <label key={tag}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={editDraft.tags.includes(tag)}
                                                                                onChange={() => toggleEditTag(tag)}
                                                                            />
                                                                            {DISPLAY_TAG_LABELS[tag]}
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {/* Description */}
                                                            <div className="edit-field" style={{ marginBottom: '0.5rem' }}>
                                                                <label>Description</label>
                                                                <textarea
                                                                    value={editDraft.description}
                                                                    onChange={e => setEditDraft(prev => ({ ...prev, description: e.target.value }))}
                                                                    placeholder="Optional description..."
                                                                    rows={2}
                                                                />
                                                            </div>

                                                            {/* Existing attached documents (read-only) */}
                                                            {hasDocuments && (
                                                                <div className="edit-field" style={{ marginBottom: '0.5rem' }}>
                                                                    <label>Existing Files</label>
                                                                    <div className="file-list">
                                                                        {incident.documents.map(doc => (
                                                                            <div key={doc.incidentDocumentId} className="file-item">
                                                                                <span className="file-icon">{getFileIcon(doc.fileName)}</span>
                                                                                <div className="file-details">
                                                                                    <span className="file-name">{doc.fileName}</span>
                                                                                    <span className="file-size">{formatFileSize(doc.fileSize)}</span>
                                                                                </div>
                                                                                {doc.fileUrl && (
                                                                                    <a
                                                                                        href={doc.fileUrl}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="btn-download"
                                                                                        onClick={e => e.stopPropagation()}
                                                                                        style={{ textDecoration: 'none' }}
                                                                                    >
                                                                                        <FaDownload />
                                                                                    </a>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Upload additional files */}
                                                            <div className="edit-field" style={{ marginBottom: '0.5rem' }}>
                                                                <label>Attach Additional Files</label>
                                                                <div
                                                                    className={`file-dropzone${editIsDragging ? ' dragging' : ''}`}
                                                                    style={{ padding: '1rem' }}
                                                                    onDragOver={e => { e.preventDefault(); setEditIsDragging(true); }}
                                                                    onDragLeave={e => { e.preventDefault(); setEditIsDragging(false); }}
                                                                    onDrop={handleEditFileDrop}
                                                                    onClick={() => editFileInputRef.current?.click()}
                                                                >
                                                                    <input
                                                                        type="file"
                                                                        ref={editFileInputRef}
                                                                        onChange={handleEditFileChange}
                                                                        multiple
                                                                        accept={editAcceptedExtensions}
                                                                        hidden
                                                                    />
                                                                    <div className="dropzone-content">
                                                                        <span className="dropzone-icon" style={{ fontSize: '1.25rem' }}>📂</span>
                                                                        <p className="dropzone-text" style={{ margin: 0 }}>
                                                                            Drag &amp; drop or <span className="dropzone-browse">browse</span> · Max: {editMaxFileSizeLabel}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                {editFiles.length > 0 && (
                                                                    <div className="file-list">
                                                                        <div className="file-list-header">
                                                                            <span>{editFiles.length} new file{editFiles.length !== 1 ? 's' : ''} to upload</span>
                                                                            <button
                                                                                type="button"
                                                                                className="clear-all-btn"
                                                                                onClick={() => { setEditFiles([]); if (editFileInputRef.current) editFileInputRef.current.value = ''; }}
                                                                            >
                                                                                Clear all
                                                                            </button>
                                                                        </div>
                                                                        {editFiles.map((file, idx) => (
                                                                            <div key={idx} className="file-item">
                                                                                <span className="file-icon">{getFileIcon(file.name)}</span>
                                                                                <div className="file-details">
                                                                                    <span className="file-name">{file.name}</span>
                                                                                    <span className="file-size">{formatFileSize(file.size)}</span>
                                                                                </div>
                                                                                <button
                                                                                    type="button"
                                                                                    className="file-remove-btn"
                                                                                    onClick={() => removeEditFile(idx)}
                                                                                    title="Remove file"
                                                                                >
                                                                                    ✕
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Actions */}
                                                            <div className="edit-actions">
                                                                <button className="btn-save" onClick={handleSaveEdit} disabled={saving}>
                                                                    {saving ? <><FaSpinner className="fa-spin" /> Saving...</> : <><FaSave /> Save</>}
                                                                </button>
                                                                <button className="btn-cancel" onClick={handleCancelEdit} disabled={saving}>
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                                {/* Expanded documents row (view mode only) */}
                                                {isExpanded && hasDocuments && !isEditing && (
                                                    <tr className="documents-row">
                                                        <td colSpan={colSpan}>
                                                            <div className="documents-list">
                                                                <strong>Attached Documents:</strong>
                                                                <ul>
                                                                    {incident.documents.map(doc => (
                                                                        <li key={doc.incidentDocumentId}>
                                                                            <FaFileAlt />
                                                                            <span className="doc-name">{doc.fileName}</span>
                                                                            <span className="doc-size">({(doc.fileSize / 1024).toFixed(1)} KB)</span>
                                                                            {doc.description && <span className="doc-desc"> — {doc.description}</span>}
                                                                            {doc.fileUrl && (
                                                                                <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="btn-download" onClick={e => e.stopPropagation()}>
                                                                                    <FaDownload /> Download
                                                                                </a>
                                                                            )}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="btn-page">
                                <FaChevronLeft /> Previous
                            </button>
                            <span className="page-info">Page {page} of {totalPages}</span>
                            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="btn-page">
                                Next <FaChevronRight />
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ─── Trend Analysis Tab ──────────────────────────────────────────────────────

const TrendAnalysisTab = ({ accessiblePropertyNames }) => {
    const { apiFetch } = useAuth();

    // Raw incidents for derived breakdowns
    const [allIncidents, setAllIncidents] = useState([]);
    // Rolling comparison data from existing endpoint
    const [trendData, setTrendData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [filterProperty, setFilterProperty] = useState('');
    const [filterTag, setFilterTag] = useState('');
    const [filterYear, setFilterYear] = useState('');

    const now = new Date();
    const currentYear = now.getFullYear();
    const period = `${currentYear}${String(now.getMonth() + 1).padStart(2, '0')}`;

    const yearOptions = [];
    for (let y = currentYear; y >= 2020; y--) yearOptions.push(y);

    const ROLLING_MONTH_NAMES = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const MONTH_ABBR = {
        January: 'Jan', February: 'Feb', March: 'Mar', April: 'Apr',
        May: 'May', June: 'Jun', July: 'Jul', August: 'Aug',
        September: 'Sep', October: 'Oct', November: 'Nov', December: 'Dec'
    };

    const TAG_STACK_COLORS = {
        'Armed Robbery': '#e74c3c',
        'Break In': '#e67e22',
        'Assault / Injury': '#f1c40f',
        'Damage To Property': '#3498db',
        'Death On Site': '#9b59b6',
        'Theft': '#1abc9c'
    };

    // Fetch rolling comparison data (unaffected by breakdown filters)
    useEffect(() => {
        let active = true;
        const run = async () => {
            try {
                setLoading(true);
                const response = await apiFetch(`/api/Ops/GetOpsIncidentReports?Period=${period}`);
                const data = await response.json();
                if (active) setTrendData(data.data);
            } catch (e) {
                Sentry.captureException(e);
                if (active) setError('Failed to load trend data.');
            } finally {
                if (active) setLoading(false);
            }
        };
        run();
        return () => { active = false; };
    }, [apiFetch, period]);

    // Fetch raw incidents for breakdown charts (filtered)
    useEffect(() => {
        let active = true;
        const run = async () => {
            try {
                const params = new URLSearchParams({ page: 1, pageSize: 9999 });
                if (filterProperty) params.append('propertyName', filterProperty);
                if (filterTag) params.append('tag', filterTag);
                if (filterYear) params.append('year', filterYear);
                const response = await apiFetch(`/api/Ops/Incidents?${params.toString()}`);
                const data = await response.json();
                if (active && data.success) setAllIncidents(data.data.incidents || []);
            } catch (e) {
                Sentry.captureException(e);
            }
        };
        run();
        return () => { active = false; };
    }, [apiFetch, filterProperty, filterTag, filterYear]);

    const clearFilters = () => { setFilterProperty(''); setFilterTag(''); setFilterYear(''); };

    // Rolling line data
    const prepareLineData = () => {
        if (!trendData) return [];
        const months = [];
        for (let i = 11; i >= 0; i--) {
            const date = new Date(currentYear, now.getMonth() - i, 1);
            months.push({ year: date.getFullYear(), month: ROLLING_MONTH_NAMES[date.getMonth()] });
        }
        const firstDate = new Date(months[0].year, ROLLING_MONTH_NAMES.indexOf(months[0].month), 1);
        const prevMonths = [];
        for (let i = 12; i >= 1; i--) {
            const date = new Date(firstDate.getFullYear(), firstDate.getMonth() - i, 1);
            prevMonths.push({ year: date.getFullYear(), month: ROLLING_MONTH_NAMES[date.getMonth()] });
        }
        return months.map((cur, idx) => {
            const prev = prevMonths[idx];
            return {
                month: `${MONTH_ABBR[cur.month]} ${cur.year}`,
                current: trendData.currentPeriodResult?.[`${cur.year} ${cur.month}`] || 0,
                previous: trendData.previousPeriodResult?.[`${prev.year} ${prev.month}`] || 0
            };
        });
    };

    // Tag breakdown
    const tagBreakdownData = useMemo(() => {
        const counts = {
            'Armed Robbery': 0, 'Break In': 0,
            'Assault / Injury': 0, 'Damage To Property': 0, 'Death On Site': 0, 'Theft': 0
        };
        allIncidents.forEach(inc => {
            if (inc.armedRobbery) counts['Armed Robbery']++;
            if (inc.breakIn) counts['Break In']++;
            if (inc.assaultOrInjury) counts['Assault / Injury']++;
            if (inc.damageToProperty) counts['Damage To Property']++;
            if (inc.deathOnSite) counts['Death On Site']++;
            if (inc.theft) counts['Theft']++;
        });
        return Object.entries(counts)
            .map(([name, value]) => ({ name, value }))
            .filter(d => d.value > 0)
            .sort((a, b) => b.value - a.value);
    }, [allIncidents]);

    // Property breakdown
    const propertyBreakdownData = useMemo(() => {
        const counts = {};
        allIncidents.forEach(inc => {
            if (inc.propertyName) counts[inc.propertyName] = (counts[inc.propertyName] || 0) + 1;
        });
        return Object.entries(counts)
            .map(([name, value]) => ({ name, value }))
            .filter(d => d.value > 0)
            .sort((a, b) => b.value - a.value)
            .slice(0, 15);
    }, [allIncidents]);

    // Monthly stacked-by-tag
    const monthlyByTagData = useMemo(() => {
        const map = {};
        allIncidents.forEach(inc => {
            const mo = inc.period % 100;
            const yr = Math.floor(inc.period / 100);
            const key = `${MONTH_ABBR[ROLLING_MONTH_NAMES[mo - 1]] || mo} ${yr}`;
            if (!map[key]) map[key] = { month: key, order: yr * 12 + mo, 'Armed Robbery': 0, 'Break In': 0, 'Assault / Injury': 0, 'Damage To Property': 0, 'Death On Site': 0, 'Theft': 0 };
            if (inc.armedRobbery) map[key]['Armed Robbery']++;
            if (inc.breakIn) map[key]['Break In']++;
            if (inc.assaultOrInjury) map[key]['Assault / Injury']++;
            if (inc.damageToProperty) map[key]['Damage To Property']++;
            if (inc.deathOnSite) map[key]['Death On Site']++;
            if (inc.theft) map[key]['Theft']++;
        });
        return Object.values(map).sort((a, b) => a.order - b.order);
    }, [allIncidents]);

    if (loading) {
        return (
            <div className="loading-container">
                <FaSpinner className="fa-spin fa-2x" />
                <p>Loading trend data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                <FaExclamationTriangle /> {error}
            </div>
        );
    }

    const lineData = prepareLineData();
    const totalCurrent = lineData.reduce((s, d) => s + d.current, 0);
    const totalPrevious = lineData.reduce((s, d) => s + d.previous, 0);
    const maxTick = Math.ceil(Math.max(...lineData.map(d => Math.max(d.current, d.previous)), 1));
    const ticks = Array.from({ length: maxTick + 1 }, (_, i) => i);
    const periodComparisonData = [
        { name: 'Current Period', value: totalCurrent },
        { name: 'Previous Period', value: totalPrevious }
    ].filter(d => d.value > 0);

    return (
        <div className="trend-analysis-tab">
            {/* Filters */}
            <div className="trend-filters">
                <div className="filter-group">
                    <label><FaFilter /> Property</label>
                    <select value={filterProperty} onChange={e => setFilterProperty(e.target.value)}>
                        <option value="">All Properties</option>
                        {accessiblePropertyNames.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Tag</label>
                    <select value={filterTag} onChange={e => setFilterTag(e.target.value)}>
                        <option value="">All Tags</option>
                        {Object.entries(TAG_LABELS_LIST).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Year</label>
                    <select value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {yearOptions.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
                <button className="btn-clear-filters" onClick={clearFilters}>Clear Filters</button>
            </div>

            <div className="charts-grid">
                {/* Rolling 12-month line — full width */}
                <div className="chart-card chart-card-wide">
                    <h3>Monthly Incident Trend (Rolling 12 Months vs Prior Year)</h3>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height={340}>
                            <LineChart data={lineData} margin={{ top: 10, right: 40, left: 10, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" interval={0} angle={-40} textAnchor="end" height={70} />
                                <YAxis domain={[0, maxTick]} ticks={ticks} />
                                <Tooltip />
                                <Legend formatter={(v) => v === 'current' ? 'Current Period' : 'Previous Period'} />
                                <Line type="monotone" dataKey="current" stroke="#e74c3c" strokeWidth={2} dot={{ r: 3 }} name="current" />
                                <Line type="monotone" dataKey="previous" stroke="#95a5a6" strokeWidth={2} dot={{ r: 3 }} name="previous" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Stacked bar by tag per month — full width */}
                {monthlyByTagData.length > 0 && (
                    <div className="chart-card chart-card-wide">
                        <h3>Incidents by Tag per Month</h3>
                        <div className="chart-container">
                            <ResponsiveContainer width="100%" height={320}>
                                <LineChart data={monthlyByTagData} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" interval={0} angle={-40} textAnchor="end" height={70} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    {Object.entries(TAG_STACK_COLORS).map(([tag, color]) => (
                                        <Line key={tag} type="monotone" dataKey={tag} stroke={color} strokeWidth={2} dot={{ r: 3 }} />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Tag breakdown horizontal bar */}
                {tagBreakdownData.length > 0 && (
                    <div className="chart-card">
                        <h3>Incidents by Tag Type</h3>
                        <div className="chart-container">
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={tagBreakdownData} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" allowDecimals={false} />
                                    <YAxis type="category" dataKey="name" width={105} tick={{ fontSize: 12 }} />
                                    <Tooltip />
                                    <Bar dataKey="value" name="Incidents" radius={[0, 4, 4, 0]}>
                                        {tagBreakdownData.map((entry, index) => (
                                            <Cell key={`tag-cell-${index}`} fill={Object.values(TAG_STACK_COLORS)[index % 5]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Property breakdown horizontal bar */}
                {propertyBreakdownData.length > 0 && (
                    <div className="chart-card">
                        <h3>Incidents by Property</h3>
                        <div className="chart-container">
                            <ResponsiveContainer width="100%" height={Math.max(280, propertyBreakdownData.length * 28 + 50)}>
                                <BarChart data={propertyBreakdownData} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" allowDecimals={false} />
                                    <YAxis type="category" dataKey="name" width={125} tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Bar dataKey="value" name="Incidents" fill="#3498db" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Period comparison pie */}
                <div className="chart-card">
                    <h3>Period Comparison</h3>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie
                                    data={periodComparisonData}
                                    cx="50%" cy="50%"
                                    labelLine
                                    label={({ name, value }) => `${name}: ${value}`}
                                    outerRadius={90}
                                    dataKey="value"
                                >
                                    {periodComparisonData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="trend-summary">
                <div className="summary-card">
                    <div className="summary-value">{totalCurrent}</div>
                    <div className="summary-label">Total Incidents (Current Period)</div>
                </div>
                <div className="summary-card">
                    <div className="summary-value">{totalPrevious}</div>
                    <div className="summary-label">Total Incidents (Previous Period)</div>
                </div>
                <div className="summary-card">
                    <div className="summary-value">
                        {(() => {
                            if (totalPrevious === 0) return totalCurrent > 0 ? '+100%' : '0%';
                            const change = ((totalCurrent - totalPrevious) / totalPrevious * 100).toFixed(0);
                            return `${change > 0 ? '+' : ''}${change}%`;
                        })()}
                    </div>
                    <div className="summary-label">Year-over-Year Change</div>
                </div>
                <div className="summary-card">
                    <div className="summary-value">
                        {lineData.length > 0 ? (totalCurrent / lineData.length).toFixed(1) : '0'}
                    </div>
                    <div className="summary-label">Monthly Average (Current)</div>
                </div>
                <div className="summary-card">
                    <div className="summary-value">{allIncidents.length}</div>
                    <div className="summary-label">Incidents in Filtered View</div>
                </div>
                <div className="summary-card">
                    <div className="summary-value">{tagBreakdownData[0]?.name || '—'}</div>
                    <div className="summary-label">Most Common Incident Type</div>
                </div>
            </div>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const IncidentReportingPage = () => {
    const { user, apiFetch, hasPermission, isEnquiryOnly } = useAuth();
    const [activeTab, setActiveTab] = useState('report');
    const [accessibleProperties, setAccessibleProperties] = useState([]);

    const canReport = hasPermission('/operations/incidents');
    const canViewReports = hasPermission('/operations/incidents');
    const canViewTrends = hasPermission('/operations/incidents');
    // Users with edit (non-enquiry-only) permission can modify existing incidents
    const canEditIncidents = hasPermission('/operations/incidents') && !isEnquiryOnly('/operations/incidents');

    // Destructure so React Compiler can track a stable primitive dependency
    const userId = user?.userID;

    // Inline async effect — avoids cascading-setState lint issue with useCallback+useEffect pattern.
    // Guard: skip the request until userId is available to avoid a request to
    // /api/Permissions/my-accessible-properties/undefined on initial render.
    useEffect(() => {
        if (!userId) return;
        let active = true;
        const run = async () => {
            try {
                const response = await apiFetch(`/api/Permissions/my-accessible-properties/${userId}`);
                const data = await response.json();
                if (!active) return;

                if (data.success) {
                    if (data.data?.unrestricted) {
                        const allPropsResponse = await apiFetch('/api/Property/all');
                        const allPropsData = await allPropsResponse.json();
                        if (active && allPropsData.success && allPropsData.data) {
                            const activeProps = [...allPropsData.data.filter(p => p.active)]
                                .sort((a, b) => (a.propertyName || '').localeCompare(b.propertyName || ''));
                            setAccessibleProperties(activeProps);
                        }
                    } else {
                        const props = data.data?.properties || [];
                        const sortedProps = [...props].sort((a, b) => (a.propertyName || '').localeCompare(b.propertyName || ''));
                        if (active) setAccessibleProperties(sortedProps);
                    }
                }
            } catch (error) {
                Sentry.captureException(error);
                if (active) toast.error('Failed to load accessible properties');
            }
        };
        run();
        return () => { active = false; };
    }, [apiFetch, userId]);

    const accessiblePropertyNames = useMemo(
        () => accessibleProperties.map(p => p.propertyName),
        [accessibleProperties]
    );

    return (
        <div className="incident-reporting-page">
            <div className="page-header">
                <div className="header-title">
                    <FaFileAlt className="title-icon" />
                    <h1>Incident Reporting</h1>
                </div>
                <p className="subtitle">
                    Report, review, and analyse operations incidents across your properties
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="tab-navigation">
                {canReport && (
                    <button
                        className={`tab-btn ${activeTab === 'report' ? 'active' : ''}`}
                        onClick={() => setActiveTab('report')}
                    >
                        <FaPlusCircle /> Report Incident
                    </button>
                )}
                {canViewReports && (
                    <button
                        className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
                        onClick={() => setActiveTab('reports')}
                    >
                        <FaListAlt /> Incident Reports
                    </button>
                )}
                {canViewTrends && (
                    <button
                        className={`tab-btn ${activeTab === 'trends' ? 'active' : ''}`}
                        onClick={() => setActiveTab('trends')}
                    >
                        <FaChartLine /> Trend Analysis
                    </button>
                )}
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {activeTab === 'report' && canReport && (
                    <ReportIncidentTab accessibleProperties={accessibleProperties} />
                )}
                {activeTab === 'reports' && canViewReports && (
                    <IncidentReportsTab accessiblePropertyNames={accessiblePropertyNames} canEdit={canEditIncidents} />
                )}
                {activeTab === 'trends' && canViewTrends && (
                    <TrendAnalysisTab accessiblePropertyNames={accessiblePropertyNames} />
                )}
            </div>
        </div>
    );
};

export default IncidentReportingPage;